import { useEffect, useState } from 'react';
import { LayoutScrollContext } from './layoutScrollContext';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { UserFeedback } from '../../components/userFeedback';
import { Box } from '@mui/material';
import { getRegistrationInfo, getToken, getUser } from '../../../services/authService';
import { DictionaryService } from '../../../services/dictionaryService';
import { useDispatch } from 'react-redux';
import { LocalStorageKey } from '../../../constants/localStorageKeys';
import {
	setBorrowerCollections,
	setInvestorCollections,
	setSytemSettingsData,
} from '../../../redux/action';
import { NewInvestorHIWDialog } from '../../components/newInvestorHiwDialog';
import { UserSurveyDialog } from '../../components/userSurveyDialog';
import { AutoInvestDialog } from '../../components/autoInvestDialog';
import { Roles } from '../../../constants/roles';
import { TokenExpiryWarningDialog } from '../../components/tokenExpiryWarningDialog';
import { InvestorService } from '../../../pages/investor/investorService';
import { DateHelper } from '../../../helpers/dateHelper';
import { buildLocalizeRoute } from '../../../helpers/languageHelper';
import { AppRoutes } from '../../../constants/appRoutes';
import { differenceInDays } from 'date-fns';
import { Helmet } from 'react-helmet-async';
import { getPersonalInformation, setPersonalInformation } from '../../../helpers/pushToGTMHelper';
import { SystemSettingsService } from '../../../services/systemSettingsService';

export const MainLayout = () => {
	const path = useLocation();
	const dictionaryService = new DictionaryService();
	const investorService = new InvestorService();
	const systemSettingsService = new SystemSettingsService();
	const [loading, setLoading] = useState(true);
	const [showAutoInvest, setShowAutoInvest] = useState(null);
	const dispatch = useDispatch();
	const location = useLocation();
	const navigate = useNavigate();
	const canonicalUrl = `${window.location.origin}${location.pathname}`;
	localStorage.setItem(LocalStorageKey.loadAllCollection, '1');
	useEffect(() => {
		window.scrollTo({
			behavior: 'smooth',
			top: 0,
			left: 0,
		});
	}, [path]);

	const isApproved = () => {
		return getUser()?.isApproved || getRegistrationInfo()?.investorStep >= 3;
	};

	const isInvestor = () => getUser() && getUser()?.role === Roles.Investor;

	useEffect(() => {
		if (!window.location.pathname.includes(AppRoutes.Login)) {
			getSettings();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!getUser()) return;

		getInvestorCollection();
		getBorrowerCollection();

		if (isInvestor()) {
			getInvestorDetails();
			if (isApproved() && getAutoInvestClose()) {
				getAutoInvestingMode();
				return;
			}
		}
		setLoading(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location]);

	const getLocalKey = () => `autoInvestClosingTime`;

	const getAutoInvestClose = () => {
		return isDifferenceGreaterThanOneWeek();
	};

	const getSettings = () => {
		systemSettingsService
			.getAllFront()
			.then((resp) => {
				dispatch(setSytemSettingsData(resp));
			})
			.catch((resp) => { });
	};

	const getInvestorDetails = () => {
		if (getPersonalInformation()) {
			return;
		}

		investorService.getInvestor().then(resp => {
			setPersonalInformation(resp.data);
		}).catch(err => { });
	};

	const isDifferenceGreaterThanOneWeek = () => {
		if (!autoInvestmentPopupClosingDate()) {
			return true;
		}

		const format = "yyyy-MM-dd";
		const dateFirst = DateHelper.formatDate(autoInvestmentPopupClosingDate(), format);
		const dateSecond = DateHelper.formatDate(new Date(), format);
		const diffDays = differenceInDays(dateFirst, dateSecond);
		return Math.abs(diffDays) >= 7;
	};

	const autoInvestmentPopupClosingDate = () => {
		return localStorage.getItem(getLocalKey())
			? localStorage.getItem(getLocalKey())
			: null;
	};

	const onCloseAutoInvestDialog = (resp) => {
		localStorage.setItem(
			getLocalKey(),
			DateHelper.formatDate(new Date(), 'yyyy-MM-dd')
		);
		setShowAutoInvest(false);
		if (resp) {
			navigate(buildLocalizeRoute(`${AppRoutes.Investor}/${AppRoutes.InvestmentSettings}?activateInvest=true`));
		}
	};

	const getAutoInvestingMode = () => {
		investorService
			.getAutoInvest()
			.then((resp) => {
				if (!resp.data?.type) {
					setTimeout(() => {
						setShowAutoInvest(true);
					}, 2000);
				} else {
					setShowAutoInvest(false);
				}
				setLoading(false);
			})
			.catch((err) => { setLoading(false); });
	};

	const getInvestorCollection = () => {
		if (!dictionaryService.getInvestorCollectionLocally()) {
			dictionaryService
				.getAllInvestorCollection()
				.then((resp) => {
					localStorage.setItem(
						LocalStorageKey.investorsCollections,
						JSON.stringify(resp.data)
					);
					dispatch(setInvestorCollections(resp.data));
				})
				.catch((err) => { });
		} else {
			dispatch(
				setInvestorCollections(dictionaryService.getInvestorCollectionLocally())
			);
		}
	};

	const getBorrowerCollection = () => {
		if (!dictionaryService.getBorrowerCollectionLocally()) {
			dictionaryService
				.getAllBorrowerCollection()
				.then((resp) => {
					localStorage.setItem(
						LocalStorageKey.borrowersCollections,
						JSON.stringify(resp.data)
					);
					dispatch(setBorrowerCollections(resp.data));
				})
				.catch((err) => { });
		} else {
			dispatch(
				setBorrowerCollections(dictionaryService.getBorrowerCollectionLocally())
			);
		}
	};

	const [scrolled, setScrolled] = useState(false);

	return (
		<LayoutScrollContext.Provider value={scrolled}>
		<Box className="relative h-full overflow-auto" onScroll={(e) => setScrolled(e.currentTarget.scrollTop > 0)}>
			<Helmet>
				<link rel="canonical" href={canonicalUrl} />
			</Helmet>
			<Outlet />
			{getToken() ? <TokenExpiryWarningDialog /> : null}
			{getToken() && isInvestor() ? <>
				{isApproved() ? <>
					{!loading ? <>
						{!showAutoInvest ? <>
							<UserSurveyDialog />
							<NewInvestorHIWDialog />
						</> : <AutoInvestDialog onClose={(e) => {
							onCloseAutoInvestDialog(e);
						}} state={showAutoInvest} />}
					</> : null}
				</> : null}
				<UserFeedback></UserFeedback>
			</> : null}
		</Box>
		</LayoutScrollContext.Provider>
	);
};
